import { Box, Link } from '@mui/material';
import { memo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * スキップナビゲーションのProps型定義
 */
type SkipNavigationProps = {
  mainContentId?: string;
  navigationId?: string;
  footerId?: string;
};

/**
 * スキップナビゲーションコンポーネント
 *
 * キーボードユーザーが主要なコンテンツエリアに直接ジャンプできるリンクを提供します。
 * WCAG 2.1のSuccess Criterion 2.4.1に準拠しています。
 *
 * @param {SkipNavigationProps} props - コンポーネントのプロパティ
 * @returns {ReactElement} スキップナビゲーション
 */
const SkipNavigation = memo(
  ({
    mainContentId = 'main-content',
    navigationId = 'main-navigation',
    footerId = 'footer',
  }: SkipNavigationProps): ReactElement => {
    const { t } = useTranslation();

    const handleSkipToContent = (event: React.MouseEvent, targetId: string) => {
      event.preventDefault();
      const target = document.getElementById(targetId);
      if (target) {
        target.setAttribute('tabindex', '-1');
        target.focus();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    return (
      <Box
        component="nav"
        aria-label={t('accessibility.skipNavigation')}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 10000,
          '& a': {
            position: 'absolute',
            left: '-10000px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            padding: '12px 24px',
            textDecoration: 'none',
            borderRadius: '0 0 8px 0',
            fontSize: '14px',
            fontWeight: 600,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease',

            '&:focus': {
              position: 'absolute',
              left: 0,
              top: 0,
              width: 'auto',
              height: 'auto',
              overflow: 'visible',
              outline: '3px solid',
              outlineColor: 'primary.dark',
              outlineOffset: '2px',
              animation: 'slideDown 0.3s ease',
            },

            '&:hover': {
              backgroundColor: 'primary.dark',
              transform: 'translateY(2px)',
            },

            '@keyframes slideDown': {
              from: {
                transform: 'translateY(-20px)',
                opacity: 0,
              },
              to: {
                transform: 'translateY(0)',
                opacity: 1,
              },
            },
          },
        }}
      >
        <Link
          href={`#${mainContentId}`}
          onClick={(e) => handleSkipToContent(e, mainContentId)}
          tabIndex={0}
        >
          {t('accessibility.skipToMainContent')}
        </Link>
        <Link
          href={`#${navigationId}`}
          onClick={(e) => handleSkipToContent(e, navigationId)}
          tabIndex={0}
        >
          {t('accessibility.skipToNavigation')}
        </Link>
        <Link href={`#${footerId}`} onClick={(e) => handleSkipToContent(e, footerId)} tabIndex={0}>
          {t('accessibility.skipToFooter')}
        </Link>
      </Box>
    );
  }
);

SkipNavigation.displayName = 'SkipNavigation';

export default SkipNavigation;
